<script setup>
import { computed, ref, watch, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ConversationCard from './widgets/conversation/ConversationCard.vue';
import ConversationCardExpanded from 'dashboard/components-next/Conversation/ConversationCard/ConversationCardExpanded.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from './widgets/conversation/contextMenu/Index.vue';
import { useFandataPerfil } from 'dashboard/composables/useFandataPerfil';
import { useFandataResolverPendente } from 'dashboard/composables/useFandataResolverPendente';
import wootConstants from 'dashboard/constants/globals';
import types from 'dashboard/store/mutation-types';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SpamAPI from 'dashboard/api/fandata/spam';

const props = defineProps({
  source: { type: Object, required: true },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  showExpanded: { type: Boolean, default: false },
});

const router = useRouter();
const store = useStore();

const { ehAdmin } = useFandataPerfil();
const { pedirEncerramento } = useFandataResolverPendente();

// T-237: o clique direito do agente fica com UM item, "Abrir em nova aba".
//
// Saem Marcar como não lida, Marcar como resolvida, Deixar pendente, Prioridade, Atribuir
// etiqueta, Atribuir agente, Atribuir time e Copiar link: são decisões de gestão, e o agente já
// tem o caminho certo para cada uma na tela da conversa.
//
// 🔴 Um deles fechava um buraco de verdade. "Marcar como resolvida" aqui chama o toggleStatus
// direto e NÃO passa pela tela de tabulação: a trava do R1.23 mora no botão Resolver, e este menu
// era um caminho paralelo que a contornava. Dava para encerrar sem tabular, exatamente o que a
// Tamara pediu que não acontecesse. Fora do menu do agente, o buraco fecha para ele. Para o
// administrador o item continua, e desde 25/09 ele também passa pela tabulação: ver o
// `onUpdateConversation` abaixo (T-234). Não sobra caminho nenhum de encerrar sem tabular.
//
// Lista vazia quer dizer "tudo liberado" (é o contrato do componente), e é o caso do administrador.
const opcoesDoCliqueDireito = computed(() =>
  // T-284: 'spam' entra na lista do agente. Marcar e desmarcar é de todos, e foi o pedido da
  // Mariana com a tela aberta: quem recebe o link é quem está atendendo.
  ehAdmin.value ? [] : ['open-new-tab', 'spam']
);

// T-284 — o estado de spam vem do CONTATO da conversa, não da conversa.
const spamDoContato = computed(
  () => props.source?.meta?.sender?.fandata_spam || 'nao'
);

const marcarSpam = async estado => {
  const contatoId = props.source?.meta?.sender?.id;
  closeContextMenu();
  if (!contatoId) return;

  try {
    await SpamAPI.definir(contatoId, estado);
    // ⚠️ RECARREGAR NÃO BASTA, E ESSA FOI A PARTE QUE EU ERREI AQUI.
    //
    // Relato do clube em 13/10: "marquei como spam e não está indo pra fila do spam e saindo da
    // minha fila". Medido em 14/10, na conversa do print: a marcação FUNCIONOU (contato 600,
    // marcado às 14:10 pela própria agente), e hoje a fila dela mostra 1 conversa e a aba Spam
    // mostra 67. O dado sempre esteve certo. Quem não mudou foi a tela, na hora.
    //
    // A causa está na montagem da lista: ela MESCLA o que o servidor responde com o que já
    // estava na memória, e só acrescenta ou substitui. Conversa que o servidor deixou de
    // responder, porque saiu do filtro, continua na tela até a página ser recarregada. Então
    // recarregar a lista não tira nada dela.
    //
    // Tirar aqui é local, e eu tinha escrito o contrário neste comentário. Mas a regra é do
    // servidor e é explícita: contato marcado sai de todas as listas, menos da aba Spam. O
    // recarregamento logo abaixo reconcilia: se o servidor discordar, as conversas voltam.
    if (estado !== 'nao') {
      store.commit(types.CLEAR_CONTACT_CONVERSATIONS, contatoId);
    }
    store.dispatch('fetchAllConversations');
    const chave = { marcado: 'MARCADO', confirmado: 'CONFIRMADO', nao: 'DESMARCADO' }[estado];
    useAlert(t(`CONVERSATION.SPAM.${chave}`));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONVERSATION.SPAM.ERRO'));
  }
};

const { t } = useI18n();

const selectConversation = inject('selectConversation');
const deSelectConversation = inject('deSelectConversation');
const assignAgent = inject('assignAgent');
const assignTeam = inject('assignTeam');
const assignLabels = inject('assignLabels');
const removeLabels = inject('removeLabels');
const updateConversationStatus = inject('updateConversationStatus');
const toggleContextMenu = inject('toggleContextMenu');
const markAsUnread = inject('markAsUnread');
const markAsRead = inject('markAsRead');
const assignPriority = inject('assignPriority');
const isConversationSelected = inject('isConversationSelected');
const deleteConversation = inject('deleteConversation');

// --- Context menu state (shared by both layouts) ---
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset context menu state when the row is recycled to a different conversation.
watch(
  () => props.source.id,
  () => {
    if (showContextMenu.value) {
      toggleContextMenu(false);
    }
    showContextMenu.value = false;
    contextMenu.value = { x: null, y: null };
  }
);

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.source.meta || {});
const assignee = computed(() => chatMetadata.value.assignee || {});
const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() =>
  senderId.value ? store.getters['contacts/getContact'](senderId.value) : {}
);

const isActiveChat = computed(() => currentChat.value.id === props.source.id);

const inbox = computed(() => {
  const inboxId = props.source.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);
const isInboxView = computed(() => !!activeInbox.value);
const showAssigneeForExpandedCard = computed(
  () => props.showExpanded || props.showAssignee
);

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.source.id,
      label: props.label,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  )
);

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  if (isActiveChat.value) return;
  router.push({ path });
};

const onExpandedSelect = checked => {
  if (checked) {
    selectConversation(props.source.id, inbox.value.id);
  } else {
    deSelectConversation(props.source.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  e.preventDefault();
  toggleContextMenu(true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  toggleContextMenu(false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();

  // T-234: "Marcar como resolvida" aqui chamava o toggleStatus direto e contornava a trava de
  // tabulação, que mora no botão Resolver. Era o único caminho por onde um encerramento escapava
  // sem ser tabulado. Agora ele ABRE a conversa e deixa o pedido registrado; a tela consome e
  // roda o encerramento completo, com tabulação e a mensagem ao torcedor.
  //
  // Os outros status (pendente, reabrir) seguem diretos: não há o que tabular neles.
  if (status === wootConstants.STATUS_TYPE.RESOLVED) {
    pedirEncerramento(props.source.id);
    if (!isActiveChat.value) router.push({ path: conversationPath.value });
    return;
  }

  updateConversationStatus(props.source.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  assignAgent(agent, [props.source.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  assignLabels([label.title], [props.source.id]);
};

const onRemoveLabel = label => {
  removeLabels([label.title], [props.source.id]);
};

const onAssignTeam = team => {
  assignTeam(team, props.source.id);
  closeContextMenu();
};

const onMarkAsUnread = () => {
  markAsUnread(props.source.id);
  closeContextMenu();
};

const onMarkAsRead = () => {
  markAsRead(props.source.id);
  closeContextMenu();
};

const onAssignPriority = priority => {
  assignPriority(priority, props.source.id);
  closeContextMenu();
};

const onDeleteConversation = () => {
  deleteConversation(props.source.id);
  closeContextMenu();
};
</script>

<template>
  <!-- Expanded layout: wide screen + expanded setting -->
  <ConversationCardExpanded
    v-if="showExpanded"
    :chat="source"
    :current-contact="currentContact"
    :assignee="assignee"
    :inbox="inbox"
    :selected="isConversationSelected(source.id)"
    :is-active-chat="isActiveChat"
    :show-assignee="showAssigneeForExpandedCard"
    :show-inbox-name="showInboxName"
    :is-inbox-view="isInboxView"
    @select-conversation="onExpandedSelect"
    @de-select-conversation="onExpandedSelect"
    @click="onCardClick"
    @contextmenu="openContextMenu"
  />

  <!-- Default (condensed) layout -->
  <ConversationCard
    v-else
    :chat="source"
    :current-contact="currentContact"
    :assignee="assignee"
    :inbox="inbox"
    :selected="isConversationSelected(source.id)"
    :is-active-chat="isActiveChat"
    :show-assignee="showAssignee"
    :show-inbox-name="showInboxName"
    @click="onCardClick"
    @contextmenu="openContextMenu"
    @select-conversation="selectConversation"
    @de-select-conversation="deSelectConversation"
  />

  <!-- Shared context menu for both layouts -->
  <ContextMenu
    v-if="showContextMenu"
    :x="contextMenu.x"
    :y="contextMenu.y"
    @close="closeContextMenu"
  >
    <ConversationContextMenu
      :allowed-options="opcoesDoCliqueDireito"
      :status="source.status"
      :inbox-id="inbox.id"
      :priority="source.priority"
      :chat-id="source.id"
      :has-unread-messages="source.unread_count > 0"
      :conversation-labels="source.labels"
      :conversation-url="conversationPath"
      :spam="spamDoContato"
      :pode-confirmar-spam="ehAdmin"
      @marcar-spam="marcarSpam"
      @update-conversation="onUpdateConversation"
      @assign-agent="onAssignAgent"
      @assign-label="onAssignLabel"
      @remove-label="onRemoveLabel"
      @assign-team="onAssignTeam"
      @mark-as-unread="onMarkAsUnread"
      @mark-as-read="onMarkAsRead"
      @assign-priority="onAssignPriority"
      @delete-conversation="onDeleteConversation"
      @close="closeContextMenu"
    />
  </ContextMenu>
</template>
